<template>
  <div
    :class="rowClass"
    role="button"
    tabindex="0"
    @click="onActivate"
    @keydown.enter.prevent="onActivate"
    @keydown.space.prevent="onActivate"
  >
    <!-- Avatar: real user → UserAvatar; system notification → "СУ" tile -->
    <UserAvatar
      v-if="notification.user_uuid"
      class="notif-avatar shrink-0"
      :name="senderName || notification.title"
      :identifier="notification.user_uuid"
      :size="28"
    />
    <div
      v-else
      class="flex h-7 w-7 shrink-0 items-center justify-center rounded font-bold text-white"
      style="
        background: linear-gradient(
          to right bottom,
          rgb(10, 129, 246),
          rgb(10, 129, 246)
        );
        font-size: 0.68rem;
      "
      :aria-label="$t('notifications.system')"
    >
      {{ $t("notifications.systemShort") }}
    </div>

    <!-- Body -->
    <div class="min-w-0 flex-1">
      <div class="text-gray-500 leading-5" style="font-size: 0.8rem">
        <div v-if="senderName" class="inline font-bold text-gray-800">
          {{ senderName }}
        </div>
        <i v-if="senderName && contentText" class="not-italic text-gray-500">
          —
        </i>
        <i class="not-italic break-all text-gray-500">
          {{ contentText || notification.title }}
        </i>
      </div>

      <div class="mt-1.5 flex flex-wrap items-center gap-0">
        <Tag
          v-if="typeLabel"
          :variant="typeVariant"
          size="xs"
          class="rounded-md"
        >
          <component v-if="typeIcon" :is="typeIcon" class="h-3 w-3" />
          {{ typeLabel }}
        </Tag>
        <i
          v-if="typeLabel"
          class="not-italic px-2 text-gray-400"
          style="font-size: 20px; line-height: 0"
          >·</i
        >
        <time
          class="text-gray-500"
          style="font-size: 0.7rem"
          :datetime="notification.created_at"
          :title="absoluteTime"
        >
          {{ relativeTime }}
        </time>
        <template v-if="canGoto">
          <i
            class="not-italic px-2 text-gray-400 hidden group-hover/notification:inline"
            style="font-size: 20px; line-height: 0"
            >·</i
          >
          <button
            type="button"
            class="hidden group-hover/notification:inline-flex items-center gap-1 text-gray-500 transition-colors hover:text-blue-500"
            style="font-size: 0.7rem"
            @click.stop="onGoto"
          >
            <SvgIcon name="sli-link" class="h-3 w-3 fill-none" />
            {{ $t("notifications.goto") }}
          </button>
        </template>
      </div>
    </div>

    <!-- Unread dot (click row to mark as read) -->
    <i
      v-if="isUnread"
      class="ml-2 mt-1.5 inline-block h-2.5 w-2.5 shrink-0 rounded-full border-2 border-blue-200 bg-blue-500"
      aria-hidden="true"
    />
  </div>
</template>

<script setup>
import { computed } from "vue";
import moment from "moment";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { storeToRefs } from "pinia";
import { UserAvatar } from "@/shared/ui/user-avatar";
import { Tag } from "@/shared/ui/tag";
import { SvgIcon } from "@/shared/ui/svg-icon";
import { useNotificationStore } from "@/entities/notification";

const props = defineProps({
  notification: { type: Object, required: true },
});

const emit = defineEmits(["mark-read"]);

const router = useRouter();
const { t } = useI18n();
const store = useNotificationStore();
const { categories } = storeToRefs(store);

const isUnread = computed(() => !props.notification.read_at);

function toTitleCase(str) {
  if (!str) return str;
  return String(str)
    .toLocaleLowerCase("ru-RU")
    .replace(
      /(^|[\s\-'’])(\p{L})/gu,
      (_, sep, ch) => sep + ch.toLocaleUpperCase("ru-RU")
    );
}

const senderName = computed(() => {
  const user = props.notification.user;
  let raw = null;
  if (user && (user.first_name || user.last_name)) {
    raw = [user.first_name, user.last_name].filter(Boolean).join(" ").trim();
  } else {
    const signer = props.notification.options?.signer;
    if (signer) {
      raw = [signer.first_name, signer.last_name]
        .filter(Boolean)
        .join(" ")
        .trim();
    } else {
      raw =
        props.notification.options?.sender_name ||
        props.notification.options?.author_name ||
        null;
    }
  }
  if (raw) return toTitleCase(raw);
  // No user/signer → it's a system notification. Use the localized label
  // ("Системные уведомления" / "Жүйелік хабарламалар") so the title row
  // still renders a bold sender prefix.
  return props.notification.user_uuid ? null : t("notifications.system");
});

// Strip "Name - " prefix from title so it doesn't repeat when we render
// senderName bold separately.
const contentText = computed(() => {
  const t = props.notification.title || "";
  const b = props.notification.body || "";
  let title = t;
  if (senderName.value) {
    const escaped = senderName.value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(`^\\s*${escaped}\\s*[—\\-:]\\s*`, "i");
    title = t.replace(re, "");
  }
  if (b && b !== title) {
    return title ? `${title} ${b}` : b;
  }
  return title;
});

const TYPE_META = Object.freeze({
  meet: { variant: "danger", icon: null, match: /meet|call|video/i },
  critical: { variant: "danger", icon: null, match: /critic|alert|error/i },
  signing: {
    variant: "success",
    icon: null,
    match: /sign|template|транз|transaction/i,
  },
  reports: { variant: "warning", icon: null, match: /report|отч/i },
  tickets: {
    variant: "primary",
    icon: null,
    match: /ticket|обращ|заявк|task|todo/i,
  },
});

const typeMeta = computed(() => {
  const t = String(props.notification.type || "").toLowerCase();
  if (!t) return null;
  for (const meta of Object.values(TYPE_META)) {
    if (meta.match.test(t)) return meta;
  }
  return { variant: "primary", icon: null };
});

const typeLabel = computed(() => {
  const type = props.notification.type;
  if (!type) return "";
  const cat = (categories.value || []).find(
    (c) => c.key === type || c.key?.toLowerCase() === String(type).toLowerCase()
  );
  if (cat?.title) return cat.title;
  return String(type).split(/[-_/\s]/)[0];
});

const typeVariant = computed(() => typeMeta.value?.variant || "primary");
const typeIcon = computed(() => typeMeta.value?.icon || null);

const canGoto = computed(() => {
  const opts = props.notification.options || {};
  return Boolean(opts.transaction_id || opts.transaction_uuid);
});

const relativeTime = computed(() => {
  const m = moment(props.notification.created_at);
  return m.isValid() ? m.fromNow() : "";
});

const absoluteTime = computed(() => {
  const m = moment(props.notification.created_at);
  return m.isValid() ? m.format("LLL") : "";
});

const rowClass = computed(() => [
  "group/notification relative flex cursor-pointer items-start gap-3 px-4 py-3 transition-colors mx-3",
  // Default: transparent border so layout stays stable, plus an ::after
  // pseudo-element acting as a real list separator at the very bottom.
  "border border-transparent rounded-lg",
  "after:absolute after:left-2 after:right-2 after:bottom-[-1px] after:h-px after:bg-border after:content-['']",
  // Hover: full border becomes visible, separator hides → no double line.
  "hover:border-border hover:bg-muted/40 hover:z-10 hover:after:hidden",
]);

function onActivate() {
  if (!props.notification.read_at) {
    emit("mark-read", props.notification.event_uuid || props.notification.uuid);
  }
}

function onGoto() {
  const opts = props.notification.options || {};
  const id = opts.transaction_id || opts.transaction_uuid;
  if (!id) return;

  // Mark read optimistically on navigation.
  if (!props.notification.read_at) {
    emit("mark-read", props.notification.event_uuid || props.notification.uuid);
  }

  const route = router.resolve({
    name: "client_document_view",
    params: { id: String(id) },
  });
  window.open(route.href, "_blank", "noopener,noreferrer");
}
</script>

<style scoped>
/* Match the "СУ" tile shape — rounded square, not a full circle. */
.notif-avatar :deep(.user-avatar) {
  border-radius: 0.25rem;
}

/* When the next sibling notification row is hovered, hide our own
   ::after separator so there's no double line under the hover. */
.group\/notification:has(+ .group\/notification:hover)::after {
  display: none;
}
</style>
